import React, { useState } from 'react';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { useQueryClient } from 'react-query';
import {
  AdminSettingsSection,
  Badge,
  Button,
  Loader,
  PageBody,
  PageTitle,
} from 'src/components';
import {
  useAiRatesQuery,
  usePlansQuery,
  useSchemaAddonQuery,
  useSubscriptionQuery,
  useTopupPacksQuery,
  useInvoicesQuery,
} from 'src/api/queries/subscriptionQueries';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { queryNames } from 'src/api/utils/queryNames';
import {
  changePlan,
  reactivateSubscription,
  setExtraSchemas,
  setExtraSeats,
  setSpendCap,
  TCatalogPlan,
  TPlanQuota,
} from 'src/api/functions/subscriptionApi';
import { TSubscription, WRITE_BLOCKING_STATUSES } from 'src/types/TSubscription';
import { FetchError } from 'src/types/TApiQueries';
import { formatBillingBytes, formatNumberIntl, formatToLocal } from 'src/utils/common';
import {
  useStripeCheckout,
  StripeCheckout,
} from 'src/hooks/useStripeCheckout';
import { useStripePayment } from 'src/hooks/useStripePayment';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { isPlanHidden, isSelfServePlan, offeredPlans } from './planVisibility';
import AiCreditCostsModal from './AiCreditCostsModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import CheckoutModal from './CheckoutModal';
import messages from './messages';
import styles from './styles.module.scss';

// Routes — the picker is its own surface (see PlanPicker.tsx), reached from
// the overview's "Choose a plan" / "Change plan" CTA.
export const SUBSCRIPTION_ROUTE = '/admin/subscription';
export const PLAN_PICKER_ROUTE = '/admin/subscription/plans';

// sessionStorage key for the purchase-toast baseline (see the effect in
// `SubscriptionContent`). Exported so the plan picker can arm it too — a
// purchase made there must still toast here on return.
export const BILLING_BASELINE_KEY = 'filespin_billing_baseline';

/**
 * Money, always to the cent: "55.50", not "55.5".
 *
 * `formatNumberIntl` drops trailing zeros, which is right for quantities and
 * for whole-dollar plan prices ("$29/month") but wrong the moment cents are in
 * play — a comparison rendered as "$55.5 vs $48" reads like a typo in exactly
 * the place a customer is checking our arithmetic.
 */
const formatUsd = (value: number): string =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

type OfferedPlan = { code: string; name: string; price: string };

// The purchasable tiers, straight from `GET /api/v1/billing/plans`
// (subscription_plans.self_serve=1, by display_order): PAYG ($0, usage-based)
// -> STARTER -> TEAM -> GROWTH -> SCALE. These were hardcoded literals until
// Phase 7 and went stale the moment Phase 1 repriced the catalog — a price
// change now reaches the UI with no frontend release. Stripe Checkout remains
// the authoritative price at payment time; these labels drive the CTA copy.
//
// `offeredPlans` drops the trial tier (you cannot "upgrade" to one) and any
// tier this deployment has retired via REACT_APP_HIDDEN_PLAN_CODES. Filtered
// HERE as well as in the picker on purpose: this list gates the "Change plan"
// CTA, so a deployment that retires everything must not offer a button that
// leads to an empty page.
const useOfferedPlans = (): OfferedPlan[] => {
  const { data } = usePlansQuery({ retry: false });
  return React.useMemo(
    () =>
      offeredPlans(data?.plans).map((p) => ({
        code: p.code,
        name: p.name,
        price: `$${formatNumberIntl(p.price_usd)}`,
      })),
    [data],
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Overview hero — plan identity + the single contextual primary action.
// Shows plan name, price, status badge, the renewal/ends date, and a
// pending-change banner. Reactivate (a cancelled sub's one recovery action)
// stays here as live state; every ROUTINE action lives in the rail's actions
// block instead.
const PlanOverview: React.FC<{
  subscription: TSubscription;
  isOwner: boolean;
}> = ({ subscription, isOwner }) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState(false);
  const [keepingPlan, setKeepingPlan] = useState(false);

  // Purchased quantity add-ons, shown under the plan as "Extras" so the
  // recurring spend beyond the plan fee is visible where the plan is.
  // Seats ride the subscription payload; schema extras need their own read
  // (the payload doesn't carry them), which the same audience may call.
  const { data: catalogForExtras } = usePlansQuery({ retry: false });
  const { data: schemaAddon } = useSchemaAddonQuery({ retry: false });
  const extraSeats = subscription.usage?.users?.extra_seats ?? 0;
  const extraSchemas = schemaAddon?.extra_schemas ?? 0;
  const seatPriceUsd = catalogForExtras?.seat_price_usd ?? SEAT_PRICE_FALLBACK_USD;
  const schemaPriceUsd =
    catalogForExtras?.schema_price_usd ?? SCHEMA_PRICE_FALLBACK_USD;

  const details = subscription.details;
  const status = subscription.status;
  const onTrial = details.is_trial || status === 'trialing';
  // Custom (negotiated) plans have codes like "CUSTOM-…"; their price is not
  // self-serve and shouldn't be surfaced as a dollar amount.
  const isCustomPlan = subscription.plan.startsWith('CUSTOM-');
  const price = isCustomPlan ? 0 : details.plan_price ?? 0;
  // PAYG's floor. 0 on every other plan, and on Enterprise we print no pricing
  // at all, so the guard covers both.
  const minimum = isCustomPlan ? 0 : details.monthly_minimum_usd ?? 0;
  const periodEnd = subscription.current_period_end
    ? new Date(subscription.current_period_end)
    : null;
  const trialEnds = subscription.trial_ends_at
    ? new Date(subscription.trial_ends_at)
    : null;
  const withinPaidWindow = periodEnd ? periodEnd.getTime() > Date.now() : true;

  const onReactivate = async () => {
    setReactivating(true);
    setReactivateError(false);
    try {
      const result = await reactivateSubscription(accessToken);
      if (isErrorResponseResult(result)) {
        setReactivateError(true);
        return;
      }
      await queryClient.invalidateQueries([queryNames.subscription]);
    } catch {
      setReactivateError(true);
    } finally {
      setReactivating(false);
    }
  };

  // Undo a scheduled change by re-selecting the current plan (backend treats
  // same-plan as "clear pending"). Lives with the pending banner it undoes.
  const keepCurrentPlan = async () => {
    setKeepingPlan(true);
    try {
      const result = await changePlan(subscription.plan, accessToken);
      if (!isErrorResponseResult(result)) {
        await queryClient.invalidateQueries([queryNames.subscription]);
      }
    } finally {
      setKeepingPlan(false);
    }
  };

  // "Renews / Active until / Trial ends" line.
  let dateLine: React.ReactNode = null;
  if (status === 'cancelled') {
    dateLine = periodEnd && (
      <FormattedMessage {...messages.overviewAccessUntil} values={{ date: periodEnd }} />
    );
  } else if (onTrial) {
    dateLine = trialEnds && (
      <FormattedMessage {...messages.overviewTrialEnds} values={{ date: trialEnds }} />
    );
  } else if (periodEnd) {
    dateLine = <FormattedMessage {...messages.overviewRenews} values={{ date: periodEnd }} />;
  }

  return (
    <div className={styles.overview}>
      <div className={styles.overviewMain}>
        <div className={styles.overviewText}>
          <div className={styles.overviewNameRow}>
            {/* A metered plan with a floor (PAYG) carries the floor IN the
                name — "Pay as you go (with $5/mo min, fully applied to your
                usage)" — one line instead of name + price span. */}
            {price <= 0 && minimum > 0 ? (
              <span className={styles.planName}>
                <FormattedMessage
                  {...messages.planNameWithMinimum}
                  values={{
                    name: details.name,
                    minimum: formatNumberIntl(minimum),
                  }}
                />
              </span>
            ) : (
              <span className={styles.planName}>{details.name}</span>
            )}
            {price > 0 && (
              <span className={styles.overviewPrice}>
                <FormattedMessage
                  {...messages.pricePerMonth}
                  values={{ price: formatNumberIntl(price) }}
                />
              </span>
            )}
          </div>
          {/* Metered-with-floor plans skip the description: PAYG's blurb
              enumerates list rates, which the "List rates" resource link
              already carries — the card stays to the point. */}
          {details.description && !(price <= 0 && minimum > 0) && (
            <p className={styles.planDescription}>{details.description}</p>
          )}
          {/* Purchased quantity add-ons — directly under the plan blurb, so
              "what the plan is" and "what you bought on top" read together;
              a rule above sets the bought things apart from the plan. */}
          {(extraSeats > 0 || extraSchemas > 0) && (
            <p
              className={cn(styles.planDescription, styles.planExtras)}
              data-test="plan-extras"
            >
              <FormattedMessage {...messages.planExtrasLabel} />{' '}
              {extraSeats > 0 && (
                <FormattedMessage
                  {...messages.planExtrasSeats}
                  values={{
                    count: extraSeats,
                    amount: formatNumberIntl(extraSeats * seatPriceUsd),
                  }}
                />
              )}
              {extraSeats > 0 && extraSchemas > 0 && ' · '}
              {extraSchemas > 0 && (
                <FormattedMessage
                  {...messages.planExtrasSchemas}
                  values={{
                    count: extraSchemas,
                    amount: formatNumberIntl(extraSchemas * schemaPriceUsd),
                  }}
                />
              )}
            </p>
          )}
          {/* Account facts, set apart from the plan blurb above: the date this
              cycle/trial ends and who owns the subscription. */}
          <div className={styles.planFacts}>
            {dateLine && <p className={styles.overviewDate}>{dateLine}</p>}
            <span className={styles.planOwner}>
              {isOwner ? (
                <FormattedMessage {...messages.ownedByYou} />
              ) : (
                <FormattedMessage
                  {...messages.ownedBy}
                  values={{ email: subscription.owner_email }}
                />
              )}
            </span>
          </div>
        </div>
        <StatusBadge status={status} dormant={subscription.dormant} />
      </div>

      {/* Scheduled (deferred) downgrade — live state, so it sits on the plan
          card together with its undo. */}
      {isOwner && subscription.pending_plan && (
        <div className={styles.pendingChange} role="status">
          <p>
            <FormattedMessage
              {...(periodEnd
                ? messages.pendingChangeBanner
                : messages.pendingChangeBannerNoDate)}
              values={{
                plan: subscription.pending_plan_name ?? subscription.pending_plan,
                effectiveAt: periodEnd ?? undefined,
              }}
            />
          </p>
          <Button
            mode="flat"
            size="small"
            onClick={keepCurrentPlan}
            disabled={keepingPlan}
            loading={keepingPlan}
          >
            <FormattedMessage {...messages.keepCurrentPlanCta} />
          </Button>
        </div>
      )}

      {/* Contextual primary action. */}
      {!isOwner ? (
        <p className={styles.planFooter}>
          <FormattedMessage
            {...messages.planChangesNonOwner}
            values={{ email: subscription.owner_email }}
          />
        </p>
      ) : status === 'cancelled' ? (
        withinPaidWindow && (
          <div className={styles.overviewCta}>
            <Button
              mode="primary"
              size="small"
              onClick={onReactivate}
              disabled={reactivating}
              loading={reactivating}
            >
              <FormattedMessage
                {...(reactivating ? messages.reactivateStarting : messages.reactivateCta)}
              />
            </Button>
            {reactivateError && (
              <p className={styles.checkoutError} role="alert">
                <FormattedMessage {...messages.reactivateError} />
              </p>
            )}
          </div>
        )
      ) : null}
      {/* The Choose/Change plan CTA lives in the rail's actions block —
          this card only reports identity and live state. */}
    </div>
  );
};

// Is cancelling available to this viewer? Owner, paid self-serve, not already
// cancelled. Exported as a predicate because two places need the answer: the
// footer that renders the link (`CancelPlanFooter`), and `BillingAndInvoices`'s own early return
// (which must not hide the section when cancel is the only thing left in it).
const canCancelPlan = (subscription: TSubscription, isOwner: boolean) => {
  const details = subscription.details;
  return Boolean(
    isOwner
      && details.self_serve
      && !details.is_trial
      && subscription.status !== 'cancelled',
  );
};

const getErrorStatus = (err: FetchError | null | undefined): number | null => {
  if (!err) return null;
  if (isErrorResponseResult(err)) return err.status;
  return null;
};

// Mapping from server status -> message key + style class. Unknown
// statuses fall back to the suspended visual so we never silently render
// an "Active" badge for a status we don't recognise.
const STATUS_DISPLAY: Record<
  string,
  { message: keyof typeof messages; className: string }
> = {
  active: { message: 'statusActive', className: 'statusActive' },
  trialing: { message: 'statusTrialing', className: 'statusTrialing' },
  past_due: { message: 'statusPastDue', className: 'statusPastDue' },
  read_only: { message: 'statusReadOnly', className: 'statusReadOnly' },
  suspended: { message: 'statusSuspended', className: 'statusSuspended' },
  cancelled: { message: 'statusCancelled', className: 'statusCancelled' },
  expired: { message: 'statusExpired', className: 'statusExpired' },
  payment_required: {
    message: 'statusPaymentRequired',
    className: 'statusPastDue',
  },
};

// A dormant account carries `status: 'read_only'`, but "Read-only" is the wrong
// word for it: nothing is wrong, it was simply idle for a cycle and billed $0.
// The badge follows `dormant`, not the status, for the same reason the banner
// does — an over-quota read_only account must keep the original label.
const StatusBadge: React.FC<{ status: string; dormant?: boolean }> = ({
  status,
  dormant,
}) => {
  const display = dormant
    ? { message: 'statusDormant' as const, className: 'statusTrialing' }
    : STATUS_DISPLAY[status] ?? STATUS_DISPLAY.suspended;
  const labelMessage = messages[display.message];
  const className = (styles as Record<string, string>)[display.className];
  return (
    <Badge className={cn(styles.statusBadge, className)}>
      <FormattedMessage {...labelMessage} />
    </Badge>
  );
};

// Customer-facing inline banner shown when the subscription's status
// blocks writes (read_only / suspended / expired) or otherwise needs
// attention (past_due / trialing).
const StatusBanner: React.FC<{
  subscription: TSubscription;
  checkout?: StripeCheckout;
}> = ({ subscription, checkout }) => {
  const history = useHistory();
  const status = subscription.status;
  const writeBlocked =
    subscription.write_blocked ??
    (WRITE_BLOCKING_STATUSES as string[]).includes(status);

  // Card-less PAYG (trial auto-converted, no payment method yet). Rendered
  // BEFORE the generic write-blocked branch: this state is self-recoverable
  // — the $0 PAYG checkout captures a card and the webhook activates — so it
  // must never show the dead-end "account suspended" copy.
  if (status === 'payment_required') {
    return (
      <div className={cn(styles.demoBanner, styles.pastDueBanner)} role="alert">
        <FormattedMessage {...messages.paymentRequiredBanner} />
        {checkout?.enabled && (
          <div className={styles.upgradeButtons}>
            <Button
              mode="primary"
              size="small"
              onClick={() => checkout.startCheckout('PAYG')}
              disabled={checkout.isLoading}
            >
              <FormattedMessage {...messages.addPaymentMethodCta} />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // DORMANT — also a write-blocked `read_only`, so this MUST precede the
  // generic branch below. Same status, entirely different situation: the
  // account was idle for a cycle, billed $0, and the next upload wakes it. The
  // read-only copy ("Contact FileSpin support") would be actively wrong here.
  if (subscription.dormant) {
    // A paused month is free only up to the plan's dormant storage allowance.
    // Past it, stored data is still billed — so the "you weren't charged" copy
    // would be something the customer discovers to be false on their invoice.
    // Decimal GB (10^9), matching how the backend meters storage.
    const allowanceGb = subscription.details.dormant_storage_gb ?? null;
    const heldGb = (subscription.usage?.storage?.used ?? 0) / 1e9;
    const billedForStorage = allowanceGb != null && heldGb > allowanceGb;
    return (
      <div className={cn(styles.demoBanner, styles.trialingBanner)} role="status">
        {billedForStorage ? (
          <FormattedMessage
            {...messages.dormantBannerBilledStorage}
            values={{ allowance: formatNumberIntl(allowanceGb as number) }}
          />
        ) : subscription.dormant_since ? (
          <FormattedMessage
            {...messages.dormantBanner}
            values={{ dormantSince: new Date(subscription.dormant_since) }}
          />
        ) : (
          <FormattedMessage {...messages.dormantBannerNoDate} />
        )}
      </div>
    );
  }

  // SPEND LIMIT — also write-blocked, so this MUST precede the generic branch
  // below. It is the one write-block the customer caused themselves and can
  // undo themselves in the next control on this very page; "contact FileSpin
  // support" would send them looking for a fault that doesn't exist.
  if (subscription.spend_cap_status?.blocked) {
    return (
      <div className={cn(styles.demoBanner, styles.readOnlyBanner)} role="alert">
        <FormattedMessage
          {...messages.spendCapBlockedBanner}
          values={{
            cap: formatUsd(subscription.spend_cap_status.cap_usd),
          }}
        />
        <div className={styles.upgradeButtons}>
          <Button
            mode="primary"
            size="small"
            /* The same `?purchase=` deep link the rail already honours, so one
               mechanism opens the limit control from anywhere. RailActions
               syncs on it, which is why this can be a route push rather than a
               reload. */
            onClick={() => history.push(`${SUBSCRIPTION_ROUTE}?purchase=spendLimit`)}
          >
            <FormattedMessage {...messages.spendCapBlockedCta} />
          </Button>
        </div>
      </div>
    );
  }

  if (writeBlocked) {
    const message =
      status === 'read_only'
        ? messages.readOnlyBannerReadOnly
        : status === 'expired'
        ? messages.readOnlyBannerExpired
        : messages.readOnlyBannerSuspended;
    return (
      <div className={cn(styles.demoBanner, styles.readOnlyBanner)} role="alert">
        <FormattedMessage {...message} />
      </div>
    );
  }
  if (status === 'past_due') {
    return (
      <div className={cn(styles.demoBanner, styles.pastDueBanner)} role="alert">
        <FormattedMessage {...messages.pastDueBanner} />
      </div>
    );
  }
  // `trialing` deliberately renders NOTHING here. The app-wide BillingBanner
  // already carries the countdown and the card CTA on every page including this
  // one, and the plan card immediately below repeats the rest ("Free trial",
  // its description, and the "Trial ends {date}" line). A third telling of the
  // same thing, stacked between the two, was just noise.
  return null;
};

type FormatFn = (value: number) => string;

// Every billing dimension renders as a stat card (like /usage): label,
// big "used" value, and the quota (or Unlimited) underneath.
type BillingCard = {
  key: string;
  label: React.ReactNode;
  value: string;
  secondary: React.ReactNode;
  tertiary?: React.ReactNode;
  // Optional smaller footnote (e.g. what consumes AI credits + a link out).
  caption?: React.ReactNode;
  // Optional corner pill distinguishing billed vs fair-use at a glance.
  // A card may set its own; the section's default is applied only when it
  // doesn't (see `capped` — seats are a hard cap, never an overage line).
  tag?: React.ReactNode;
  tagKind?: 'billed' | 'capped';
};

const CARD_TAG_STYLE = {
  capped: 'cardTagCapped',
  billed: 'cardTagBilled',
} as const;

const BillingCardView: React.FC<{ card: BillingCard }> = ({ card }) => (
  <div className={styles.billingCard}>
    <div className={styles.billingCardHead}>
      <span className={styles.billingCardTitle}>{card.label}</span>
      {card.tag && (
        <span
          className={cn(styles.cardTag, styles[CARD_TAG_STYLE[card.tagKind ?? 'billed']])}
        >
          {card.tag}
        </span>
      )}
    </div>
    <span className={styles.billingCardValue}>{card.value}</span>
    <span className={styles.billingCardSecondary}>{card.secondary}</span>
    {card.tertiary && (
      <span className={styles.billingCardSecondary}>{card.tertiary}</span>
    )}
    {card.caption && (
      <span className={styles.billingCardCaption}>{card.caption}</span>
    )}
  </div>
);

const buildBillingCards = (
  subscription: TSubscription,
  videoUnit: string,
  // The current plan's catalog entry, when loaded. Used only to tell the two
  // meanings of a ZERO allowance apart — see `meteredDims` below.
  catalogPlan?: TCatalogPlan,
): BillingCard[] => {
  const { usage, details } = subscription;
  const integerFmt: FormatFn = (v: number) => formatNumberIntl(v);
  const minutesFmt: FormatFn = (v: number) => `${formatNumberIntl(v)} ${videoUnit}`;

  // Dimensions this plan bills from the first unit. A quota of 0 on one of
  // these means "metered", NOT "unlimited" — on PAYG (every allowance is 0)
  // the old wording promised unlimited storage, transcoding and delivery.
  const meteredDims = new Set(
    Object.entries(catalogPlan?.quotas ?? {})
      .filter(([, q]) => (q as TPlanQuota).included === 0
        && (q as TPlanQuota).overage_rate_usd)
      .map(([dim]) => dim),
  );

  const cardOf = (
    key: string,
    label: React.ReactNode,
    used: number,
    quota: number,
    format: FormatFn,
    // Catalog dimension backing this card, when the names differ.
    dim: string = key,
  ): BillingCard => ({
    key,
    label,
    value: format(used),
    secondary:
      quota !== 0 ? (
        <FormattedMessage {...messages.ofQuota} values={{ quota: format(quota) }} />
      ) : meteredDims.has(dim) ? (
        <FormattedMessage {...messages.meteredAtListRates} />
      ) : (
        <FormattedMessage {...messages.unlimited} />
      ),
  });

  const cards: BillingCard[] = [];

  // Billed dimensions: Users, Storage, Video transcode minutes (Custom plans
  // only; elsewhere video is paid in credits), CDN delivery, credits. Assets,
  // ODI transforms and Print Export are NOT shown: they are neither billed
  // nor (on paid plans) enforced, so a card reporting them only invited "am I
  // about to be charged for this?".
  // Seats are a HARD CAP, not an overage dimension: you cannot exceed them
  // (the invite is refused), so this card must not wear the "Billed" pill the
  // rest of this group carries. It shows the contributor seats that the plan
  // charges for, with the free viewer allowance underneath.
  const seats = usage.users;
  const contributorQuota = seats.contributor_quota ?? seats.quota;
  cards.push({
    key: 'users',
    label: <FormattedMessage {...messages.usageUsers} />,
    value: integerFmt(seats.contributors ?? seats.used),
    // NB: values consumed by an ICU `plural` (`#`) must be NUMBERS, not
    // pre-formatted strings — react-intl throws on a string there.
    secondary:
      contributorQuota === 0 ? (
        <FormattedMessage {...messages.unlimited} />
      ) : (
        <FormattedMessage
          {...messages.seatsOfContributorSeats}
          values={{ quota: contributorQuota }}
        />
      ),
    tertiary:
      seats.viewers === undefined ? undefined : seats.viewer_quota ? (
        <FormattedMessage
          {...messages.seatsViewersLine}
          values={{
            used: integerFmt(seats.viewers),
            quota: seats.viewer_quota,
          }}
        />
      ) : (
        <FormattedMessage
          {...messages.seatsViewersLineUnlimited}
          values={{ used: integerFmt(seats.viewers) }}
        />
      ),
    caption:
      seats.extra_seats && seats.extra_seats > 0 ? (
        <>
          <FormattedMessage
            {...messages.seatsExtraNote}
            values={{
              included: integerFmt(seats.contributors_included ?? 0),
              extra: seats.extra_seats,
            }}
          />
          {seats.viewers_per_seat ? (
            <>
              {' '}
              <FormattedMessage
                {...messages.seatsPerSeatViewersNote}
                values={{ count: seats.viewers_per_seat }}
              />
            </>
          ) : null}
        </>
      ) : seats.viewers_per_seat ? (
        // Derived viewer cap (PAYG): make the 10-per-seat rule legible —
        // buying a seat is also how you get more viewers.
        <FormattedMessage
          {...messages.seatsPerSeatViewersNote}
          values={{ count: seats.viewers_per_seat }}
        />
      ) : undefined,
    tag: <FormattedMessage {...messages.seatCapTag} />,
    tagKind: 'capped',
  });

  cards.push(
    cardOf(
      'storage',
      <FormattedMessage {...messages.usageStorage} />,
      usage.storage.used,
      usage.storage.quota,
      (v: number) => formatBillingBytes(v),
      'storage_gb',
    ),
  );
  // Video is paid in credits on self-serve plans (conf/22): its usage lands in
  // the credits card, and a minutes allowance of 0 would only read as
  // "Unlimited". Custom contracts keep their transcode-minutes allowance.
  if (details.transcode_billing !== 'credits') {
    cards.push(
      cardOf(
        'video_minutes',
        <FormattedMessage {...messages.usageVideo} />,
        usage.video_minutes.used,
        usage.video_minutes.quota,
        minutesFmt,
        'transcode_minutes',
      ),
    );
  }

  // CDN delivery (egress) — key billing dimension. The API reports raw
  // bytes for the current cycle; quota 0 means unlimited. Always shown
  // (even at 0) so subscribers see the dimension exists on their plan.
  // The tertiary line spells out the window because the /usage page
  // measures a different one (selected range, per hostname).
  if (usage.cdn) {
    cards.push({
      ...cardOf(
        'cdn',
        <FormattedMessage {...messages.usageCdn} />,
        usage.cdn.used,
        usage.cdn.quota,
        (v: number) => formatBillingBytes(v),
        'cdn_bytes',
      ),
      tertiary: <FormattedMessage {...messages.cdnCycleWindow} />,
    });
  }

  // Live-count entitlement caps (rollout 28). Like seats these are HARD
  // CAPS with an upgrade prompt, never overage lines — so they wear the
  // "capped" pill, and quota 0 renders "Unlimited" (they carry no overage
  // rate, so meteredDims can't claim them). Guarded on presence: responses
  // predating rollout 28 omit the keys.
  if (usage.collections) {
    cards.push({
      ...cardOf(
        'collections',
        <FormattedMessage {...messages.usageCollections} />,
        usage.collections.used,
        usage.collections.quota,
        integerFmt,
      ),
      tag: <FormattedMessage {...messages.planLimitTag} />,
      tagKind: 'capped',
    });
  }
  if (usage.shares) {
    cards.push({
      ...cardOf(
        'shares',
        <FormattedMessage {...messages.usageShares} />,
        usage.shares.used,
        usage.shares.quota,
        integerFmt,
      ),
      tag: <FormattedMessage {...messages.planLimitTag} />,
      tagKind: 'capped',
    });
  }

  // NOTE: per-addon request counts are intentionally NOT shown here. Their
  // cost is already captured by a billing dimension — AI-metered addons
  // (background removal, image analysis, face recognition, AI edits) draw
  // down the AI credits card below; video transcode is the minutes card; the
  // rest (ODI, print export, delivery insights) are fair-use / tracking-only.
  // The billing summary shows what you're billed on or capped by; the
  // per-feature request breakdown lives in Overview → Addon requests.

  // Only show AI credits when the plan grants them or the account has a
  // running balance (e.g. credits topped up by support).
  const hasAi =
    details.ai_credits_initial > 0 ||
    usage.ai_credits.balance > 0 ||
    usage.ai_credits.used > 0;
  if (hasAi) {
    // Balance-led card: the big number is the total spendable balance, split
    // into plan allowance vs purchased top-ups (they sum to the balance). The
    // recurring monthly allowance is shown as context in the caption.
    const balance = usage.ai_credits.balance;
    const topup = usage.ai_credits.topup_balance ?? 0;
    const planPortion = Math.max(balance - topup, 0);
    cards.push({
      key: 'ai_credits',
      label: <FormattedMessage {...messages.usageAiCredits} />,
      value: integerFmt(balance),
      secondary: <FormattedMessage {...messages.aiCreditsRemainingLabel} />,
      tertiary:
        topup > 0 ? (
          <FormattedMessage
            {...messages.aiCreditsBreakdown}
            values={{
              plan: integerFmt(planPortion),
              topup: integerFmt(topup),
            }}
          />
        ) : (
          <FormattedMessage {...messages.aiCreditsFromPlan} />
        ),
      caption: (
        <>
          {usage.ai_credits.quota > 0 && (
            <>
              <FormattedMessage
                // A trial's credits are a one-off grant, not a monthly one.
                {...(details.is_trial ? messages.aiCreditsTrialNote : messages.aiCreditsMonthlyNote)}
                values={{ quota: formatNumberIntl(usage.ai_credits.quota) }}
              />
              {'. '}
            </>
          )}
          <FormattedMessage
            // A trial's print exports stop at the cap (never charged), so
            // only a paid plan's credits cover exports past the allowance.
            {...(catalogPlan?.quotas?.print_exports && !catalogPlan.is_trial
              ? (details.transcode_billing === 'credits'
                ? messages.aiCreditsIncludesVideoWithPrint
                : messages.aiCreditsIncludesWithPrint)
              : (details.transcode_billing === 'credits'
                ? messages.aiCreditsIncludesVideo
                : messages.aiCreditsIncludes))}
          />
        </>
      ),
    });
  }

  return cards;
};

// DEMO-only hard caps. `asset_count` and `addon_PRINT_EXPORT` are fair use on
// every paid plan and are deliberately not rendered there — but on the trial
// the plan's `block` enforcement makes both real walls (uploads refused at the
// asset cap, exports refused at 20/cycle), so a trial must be able to see them
// coming. Never billed, so they wear the `capped` pill rather than "Billed".
//
// Returns [] off-trial, which is what keeps this out of the paid experience.
const buildTrialLimitCards = (subscription: TSubscription): BillingCard[] => {
  const { usage } = subscription;

  const ofQuota = (quota: number) => (
    <FormattedMessage
      {...messages.ofQuota}
      values={{ quota: formatNumberIntl(quota) }}
    />
  );

  const cards: BillingCard[] = [
    {
      key: 'assets',
      label: <FormattedMessage {...messages.usageAssets} />,
      value: formatNumberIntl(usage.assets.used),
      secondary: usage.assets.quota > 0 ? ofQuota(usage.assets.quota) : undefined,
    },
  ];

  // Print Export is a per-cycle allowance on DEMO (20). Shown only when the
  // plan actually carries the addon.
  const printExport = usage.addons?.find((a) => a.code === 'PRINT_EXPORT');
  if (printExport) {
    cards.push({
      key: 'print_export',
      label: <FormattedMessage {...messages.usagePrintExport} />,
      value: formatNumberIntl(printExport.used),
      secondary: printExport.quota > 0 ? ofQuota(printExport.quota) : undefined,
    });
  }

  return cards;
};

// Fallback price of one extra contributor seat (USD/month) for the brief
// window before the catalog query resolves. The authoritative figure is
// `seat_price_usd` from GET /api/v1/billing/plans; Stripe is the billing
// authority either way.
const SEAT_PRICE_FALLBACK_USD = 9;
// Last-resort fallback for the PAYG schema add-on price — the catalog's
// `schema_price_usd` always wins (single backend source, migration 54).
const SCHEMA_PRICE_FALLBACK_USD = 5;

// Human labels for the metered dimensions, in the order the rate card reads
// best: what you store, what you process, what you deliver, what AI costs.
const RATE_CARD_DIMENSIONS = [
  'storage_gb',
  'transcode_minutes',
  'cdn_bytes',
  'ai_credits',
] as const;

// The list rates the current plan bills overage at — the "metered at list
// rates" panel the PAYG tier needs, and useful context on every paid plan.
// Rates and units both come from the catalog: `cdn_bytes` is allowanced in
// BYTES but charged per GB, so the rate's own `rate_unit` is what we print.
const RateCardBody: React.FC<{ planCode: string; onTrial?: boolean }> = ({
  planCode,
  onTrial,
}) => {
  const { data } = usePlansQuery({ retry: false });
  const { data: aiRates } = useAiRatesQuery();
  const { showModal } = useModal();

  // A trial bills nothing, so its own rates are all zero — but the trial now
  // CONVERTS to Pay as you go, and the banner says so. Showing PAYG's rates
  // here answers the obvious next question ("what will that cost me?")
  // instead of leaving the section blank.
  const showingFuturePaygRates = Boolean(onTrial);
  const plan = data?.plans.find(
    (p) => p.code === (showingFuturePaygRates ? 'PAYG' : planCode),
  );
  if (!plan) return null;

  const rows = RATE_CARD_DIMENSIONS
    .map((dim) => ({ dim, quota: plan.quotas[dim] }))
    .filter((r) => r.quota?.overage_rate_usd);
  if (rows.length === 0) return null;

  // No heading of its own: its reveal control supplies one (the "List
  // rates" link in the rail's Resources block; the picker wraps it in a
  // titled Fold).
  // The floor belongs with the rates, not apart from them: "from $5/mo" is
  // meaningless without the list rates it is applied to, and the rates alone
  // read as "$0 if I do nothing", which is only true of an idle month.
  const minimum = plan.monthly_minimum_usd ?? 0;

  return (
    <div className={styles.buyCreditsBlock}>
      <p className={styles.billingSubtitle}>
        <FormattedMessage
          {...(showingFuturePaygRates
            ? messages.rateCardSubtitleTrial
            : messages.rateCardSubtitle)}
        />
      </p>
      {minimum > 0 && (
        <p className={styles.billingSubtitle}>
          <FormattedMessage
            {...messages.rateCardMinimumNote}
            values={{ minimum: formatNumberIntl(minimum) }}
          />
        </p>
      )}
      <ul className={styles.rateCardList}>
        {rows.map(({ dim, quota }) => (
          <li key={dim} className={styles.rateCardRow}>
            <span>
              <FormattedMessage {...(messages as any)[`rateDim_${dim}`]} />
            </span>
            <span className={styles.rateCardPrice}>
              <FormattedMessage
                {...messages.rateCardPerUnit}
                values={{
                  price: quota!.overage_rate_usd,
                  unit: quota!.rate_unit,
                }}
              />
            </span>
          </li>
        ))}
        {/* conf/22: video is paid in credits, per minute of uploaded video —
            the full per-format list is in the "what credits buy" modal. */}
        {plan.transcode_billing === 'credits' && aiRates?.flat?.video_sd !== undefined && (
          <li className={styles.rateCardRow}>
            <span>
              <FormattedMessage {...messages.rateDim_transcode_minutes} />
            </span>
            <span className={styles.rateCardPrice}>
              <FormattedMessage
                {...messages.rateCardVideoInCredits}
                values={{ from: aiRates.flat.video_sd }}
              />
            </span>
          </li>
        )}
      </ul>
      {/* The other half of the pricing story: the dimensions above say what
          OVERAGE costs, the modal says what credits BUY. */}
      <button
        type="button"
        className={styles.cardAction}
        onClick={() => showModal(<AiCreditCostsModal />)}
      >
        <FormattedMessage {...messages.aiCostsLink} />
      </button>
    </div>
  );
};

// "This month would have cost $X on Starter" — the PAYG upgrade nudge.
//
// The honesty rule lives on the SERVER: `upgrade_nudge` is populated only when
// a plan is genuinely cheaper for this cycle's consumption (storage-only usage,
// for instance, is often cheaper on PAYG than on any tier, and there the field
// is simply absent). So this renders whatever it is given and never re-derives
// the comparison — two implementations of that maths would eventually disagree,
// and the UI's copy would be the one that lied.
const UpgradeNudge: React.FC<{ subscription: TSubscription }> = ({
  subscription,
}) => {
  const history = useHistory();
  const nudge = subscription.upgrade_nudge;
  // The server computes this against the CATALOG, which knows nothing about
  // what this deployment currently sells — so a retired tier still arrives
  // here, and "this month would have cost $39 on Starter" would send the
  // reader to a picker with nothing in it. Checked per plan_code rather than
  // against the whole offer: correct whether one tier is retired or all.
  if (!nudge || isPlanHidden(nudge.plan_code)) return null;

  return (
    <div className={styles.buyCreditsBlock}>
      <h4 className={styles.usageGroupHeading}>
        <FormattedMessage {...messages.nudgeHeading} />
      </h4>
      <p className={styles.billingSubtitle}>
        <FormattedMessage
          {...messages.nudgeBody}
          values={{
            current: formatUsd(nudge.current_usd),
            planName: nudge.plan_name,
            alternative: formatUsd(nudge.alternative_usd),
            saving: formatUsd(nudge.saving_usd),
          }}
        />
      </p>
      <Button
        mode="secondary"
        size="small"
        onClick={() => history.push(PLAN_PICKER_ROUTE)}
      >
        <FormattedMessage {...messages.nudgeCta} />
      </Button>
    </div>
  );
};

// Owner-facing spend limit — the customer half of "pay as you go, no surprise
// bills", and a HARD limit: at 100% the server stops accepting uploads and
// processing until the owner raises it or the cycle resets.
//
// Three things the copy has to carry, because none is guessable and each is a
// support ticket when missed:
//   1. reaching the limit STOPS work. It used to only shrink the invoice, and
//      the old copy said so in as many words ("nothing stops working when you
//      reach it") — which is now exactly backwards;
//   2. what stops and what doesn't. "Uploads paused" reads as data loss unless
//      you say the assets are fine and delivery keeps running;
//   3. it covers the WHOLE bill, plan fee included, so on a paid plan it
//      cannot go below the plan price (the server rejects it, so we say so
//      before the round-trip rather than surfacing a 400).
const SpendLimitBody: React.FC<{ subscription: TSubscription }> = ({
  subscription,
}) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showAlert } = useAlerts();
  const { formatMessage } = useIntl();
  const current = subscription.spend_cap_usd ?? null;
  // The floor is max(plan fee, monthly minimum), computed server-side. Fall
  // back to the monthly minimum for a response that predates the field, which
  // is what the floor WAS — an old client then bounds itself as it always did
  // and the server still has the last word.
  const minimum = subscription.spend_cap_minimum_usd
    ?? subscription.details.monthly_minimum_usd
    ?? 0;
  const planFee = subscription.details.plan_price ?? 0;
  const status = subscription.spend_cap_status ?? null;
  const [draft, setDraft] = useState<string>(current != null ? String(current) : '');
  const [saving, setSaving] = useState(false);

  const commit = async (value: number | null) => {
    if (value != null && minimum > 0 && value < minimum) {
      showAlert(formatMessage(messages.spendCapMinimum, { minimum }));
      return;
    }
    setSaving(true);
    try {
      const result = await setSpendCap(value, accessToken);
      if (isErrorResponseResult(result)) {
        const body = (result as { error?: unknown }).error;
        showAlert(
          typeof body === 'string' ? body : formatMessage(messages.spendCapError),
        );
        return;
      }
      showSuccess(
        value == null
          ? formatMessage(messages.spendCapCleared)
          : formatMessage(messages.spendCapSaved, { amount: formatUsd(value) }),
      );
      if (value == null) setDraft('');
      await queryClient.invalidateQueries([queryNames.subscription]);
    } catch {
      showAlert(formatMessage(messages.spendCapError));
    } finally {
      setSaving(false);
    }
  };

  const parsed = Number(draft);
  const canSave = draft.trim() !== '' && Number.isFinite(parsed) && parsed >= 0
    && parsed !== current;

  return (
    <div className={styles.buyCreditsBlock}>
      <p className={styles.billingSubtitle}>
        <FormattedMessage {...messages.spendCapSubtitle} />
      </p>
      <p className={styles.billingSubtitle}>
        {current != null ? (
          <FormattedMessage
            {...messages.spendCapCurrent}
            values={{ amount: formatUsd(current) }}
          />
        ) : (
          <FormattedMessage {...messages.spendCapNone} />
        )}
      </p>

      {/* Where this cycle stands. A limit you cannot watch yourself approach
          is one you only find out about when it stops you — and the bar is
          also how a customer sees that the plan fee counts toward it. */}
      {status && (
        <div className={styles.spendCapMeter}>
          <div
            className={cn(
              styles.spendCapTrack,
              status.blocked && styles.spendCapTrackBlocked,
            )}
            role="progressbar"
            aria-label={formatMessage(messages.spendCapProgressLabel)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(status.pct)}
          >
            <div
              className={styles.spendCapFill}
              /* Clamped: past 100% the bar is full, not overflowing its box. */
              style={{ width: `${Math.min(100, Math.max(0, status.pct))}%` }}
            />
          </div>
          <p className={styles.railNote}>
            <FormattedMessage
              {...messages.spendCapProgress}
              values={{
                spend: formatUsd(status.spend_usd),
                cap: formatUsd(status.cap_usd),
                pct: Math.round(status.pct),
              }}
            />
          </p>
        </div>
      )}

      {status?.blocked && (
        <p className={styles.spendCapBlockedNote} role="alert">
          <FormattedMessage {...messages.spendCapReachedNote} />
        </p>
      )}

      <div className={styles.seatsRow}>
        <input
          type="number"
          className={styles.spendCapInput}
          min={minimum || 0}
          step="1"
          value={draft}
          disabled={saving}
          placeholder={formatMessage(messages.spendCapPlaceholder)}
          aria-label={formatMessage(messages.spendCapTitle)}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button
          mode="primary"
          size="small"
          disabled={saving || !canSave}
          onClick={() => commit(parsed)}
        >
          <FormattedMessage {...messages.spendCapSave} />
        </Button>
        {current != null && (
          <Button
            mode="tertiary"
            size="small"
            disabled={saving}
            onClick={() => commit(null)}
          >
            <FormattedMessage {...messages.spendCapClear} />
          </Button>
        )}
      </div>

      {/* Only on a plan with a fee: on PAYG the floor is the monthly minimum,
          which the plan card already explains, and repeating it here as a
          "plan fee" would contradict the one thing that copy must not say. */}
      {planFee > 0 && (
        <p className={styles.railNote}>
          <FormattedMessage
            {...messages.spendCapIncludesPlanFee}
            values={{ fee: formatUsd(planFee), minimum: formatUsd(minimum) }}
          />
        </p>
      )}

      <p className={styles.railNote}>
        <FormattedMessage {...messages.spendCapServiceNote} />
      </p>
    </div>
  );
};

// Owner-facing seat management — plan-structure v2 Phase 7.
//
// Contributor seats (ADMIN/MANAGER/CREATOR) are capped at what the plan
// includes plus any bought at $9/mo; viewers draw on a separate free
// allowance. This block is where the owner buys or releases the extras.
//
// The count sent is ABSOLUTE (it mirrors the Stripe item quantity), so the
// stepper edits a local draft and one "Update seats" click commits it —
// clicking + three times must not fire three prorated Stripe writes.
const SeatsBody: React.FC<{ subscription: TSubscription }> = ({ subscription }) => {
  const seats = subscription.usage.users;
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showAlert } = useAlerts();
  const { formatMessage } = useIntl();
  const { data: catalog } = usePlansQuery({ retry: false });
  const seatPriceUsd = catalog?.seat_price_usd ?? SEAT_PRICE_FALLBACK_USD;
  const [draft, setDraft] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const included = seats.contributors_included ?? 0;
  const current = seats.extra_seats ?? 0;
  const inUse = seats.contributors ?? 0;
  const pending = draft ?? current;
  const dirty = pending !== current;

  // Plans with an unlimited contributor allowance (CUSTOM/Enterprise) have
  // nothing to buy; the server rejects the purchase outright.
  //
  // Trials are excluded too: there is no card on file and nothing to prorate
  // against, so "+$9/month" would be a promise we cannot keep. The right move
  // from a trial is to pick a plan, which the Overview CTA already offers.
  const onTrial = subscription.details.is_trial || subscription.status === 'trialing';
  if (included <= 0 || onTrial) return null;

  // Never offer a reduction that would leave the account over its cap — the
  // server rejects it, and saying so up front beats a 400.
  const minSeats = Math.max(0, inUse - included);
  const delta = pending - current;

  const commit = async () => {
    setSaving(true);
    try {
      const result = await setExtraSeats(pending, accessToken);
      if (isErrorResponseResult(result)) {
        const body = (result as { error?: unknown }).error;
        showAlert(
          typeof body === 'string' ? body : formatMessage(messages.seatsUpdateFailed),
        );
        return;
      }
      showSuccess(formatMessage(messages.seatsUpdated, { count: pending }));
      setDraft(null);
      await queryClient.invalidateQueries([queryNames.subscription]);
    } catch {
      showAlert(formatMessage(messages.seatsUpdateFailed));
    } finally {
      setSaving(false);
    }
  };

  // Opened from the seats usage card, which supplies the heading — so this
  // renders the controls only. The subtitle stays: it carries the $9/mo price
  // and the viewers-are-free rule, neither of which is inferable.
  return (
    <div className={styles.buyCreditsBlock}>
      <p className={styles.billingSubtitle}>
        <FormattedMessage
          {...messages.seatsSectionSubtitle}
          values={{ included, price: seatPriceUsd }}
        />
      </p>

      <div className={styles.seatsRow}>
        <div className={styles.seatsStepper}>
          <Button
            mode="tertiary"
            size="small"
            disabled={saving || pending <= minSeats}
            onClick={() => setDraft(Math.max(minSeats, pending - 1))}
          >
            −
          </Button>
          <span className={styles.seatsCount}>{pending}</span>
          <Button
            mode="tertiary"
            size="small"
            disabled={saving}
            onClick={() => setDraft(pending + 1)}
          >
            +
          </Button>
        </div>

        <span className={styles.seatsSummary}>
          <FormattedMessage
            {...messages.seatsTotalLine}
            values={{ total: included + pending, inUse }}
          />
        </span>

        {dirty && (
          <>
            <span className={styles.seatsDelta}>
              <FormattedMessage
                {...(delta > 0 ? messages.seatsCostIncrease : messages.seatsCostDecrease)}
                values={{ amount: Math.abs(delta) * seatPriceUsd }}
              />
            </span>
            <Button mode="primary" size="small" loading={saving} onClick={commit}>
              <FormattedMessage {...messages.seatsUpdateCta} />
            </Button>
            <Button
              mode="tertiary"
              size="small"
              disabled={saving}
              onClick={() => setDraft(null)}
            >
              <FormattedMessage {...messages.seatsCancelCta} />
            </Button>
          </>
        )}
      </div>

      {pending > current && (
        <p className={styles.billingCardCaption}>
          <FormattedMessage {...messages.seatsProrationNote} />
        </p>
      )}
    </div>
  );
};

// PAYG asset-schema add-on (migration 54) — SeatsBody's contract, cloned,
// plus the one piece seats lack: a CEILING. The plan sells at most
// `max_extra` extras (PAYG: 6 → 1 included + 6 = 7 total), so "+" disables
// at the top the way "−" disables at the floor. The count sent is ABSOLUTE
// (mirrors the Stripe item quantity); one "Update" click commits the draft.
const SchemasBody: React.FC<{ subscription: TSubscription }> = ({ subscription }) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showAlert } = useAlerts();
  const { formatMessage } = useIntl();
  const { data: catalog } = usePlansQuery({ retry: false });
  const schemaPriceUsd = catalog?.schema_price_usd ?? SCHEMA_PRICE_FALLBACK_USD;
  const { data: addon } = useSchemaAddonQuery();
  const [draft, setDraft] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Same exclusions as seats: unlimited plans have nothing to buy, plans
  // with max_extra 0 don't sell the add-on (the server answers 400), and a
  // trial has no card to prorate against.
  const onTrial = subscription.details.is_trial || subscription.status === 'trialing';
  if (!addon || addon.included <= 0 || addon.max_extra <= 0 || onTrial) return null;

  const included = addon.included;
  const current = addon.extra_schemas;
  const pending = draft ?? current;
  const dirty = pending !== current;
  // Floor: never offer a reduction the server would refuse (active schemas
  // above the reduced cap). Ceiling: the plan's purchasable maximum.
  const minExtra = Math.max(0, addon.active_schemas - included);
  const maxExtra = addon.max_extra;
  const delta = pending - current;

  const commit = async () => {
    setSaving(true);
    try {
      const result = await setExtraSchemas(pending, accessToken);
      if (isErrorResponseResult(result)) {
        const body = (result as { error?: unknown }).error;
        showAlert(
          typeof body === 'string' ? body : formatMessage(messages.schemasUpdateFailed),
        );
        return;
      }
      showSuccess(formatMessage(messages.schemasUpdated, { count: pending }));
      setDraft(null);
      await queryClient.invalidateQueries([queryNames.schemaAddon]);
      await queryClient.invalidateQueries([queryNames.subscription]);
    } catch {
      showAlert(formatMessage(messages.schemasUpdateFailed));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.buyCreditsBlock}>
      <p className={styles.billingSubtitle}>
        <FormattedMessage
          {...messages.schemasSectionSubtitle}
          values={{
            included,
            price: schemaPriceUsd,
            max: included + maxExtra,
          }}
        />
      </p>

      <div className={styles.seatsRow}>
        <div className={styles.seatsStepper}>
          <Button
            mode="tertiary"
            size="small"
            disabled={saving || pending <= minExtra}
            onClick={() => setDraft(Math.max(minExtra, pending - 1))}
          >
            −
          </Button>
          <span className={styles.seatsCount}>{pending}</span>
          <Button
            mode="tertiary"
            size="small"
            disabled={saving || pending >= maxExtra}
            onClick={() => setDraft(Math.min(maxExtra, pending + 1))}
          >
            +
          </Button>
        </div>

        <span className={styles.seatsSummary}>
          <FormattedMessage
            {...messages.schemasTotalLine}
            values={{ total: included + pending, inUse: addon.active_schemas }}
          />
        </span>

        {dirty && (
          <>
            <span className={styles.seatsDelta}>
              <FormattedMessage
                {...(delta > 0 ? messages.seatsCostIncrease : messages.seatsCostDecrease)}
                values={{ amount: Math.abs(delta) * schemaPriceUsd }}
              />
            </span>
            <Button mode="primary" size="small" loading={saving} onClick={commit}>
              <FormattedMessage {...messages.schemasUpdateCta} />
            </Button>
            <Button
              mode="tertiary"
              size="small"
              disabled={saving}
              onClick={() => setDraft(null)}
            >
              <FormattedMessage {...messages.seatsCancelCta} />
            </Button>
          </>
        )}
      </div>

      {pending > current && (
        <p className={styles.billingCardCaption}>
          <FormattedMessage {...messages.seatsProrationNote} />
        </p>
      )}
    </div>
  );
};

// Owner-facing "Buy AI credits" block — a card per top-up pack (from the
// catalog) that opens Stripe Checkout. Rendered INSIDE the Plan section (not
// a standalone section). Shares the parent's Stripe checkout hook so the
// purchase notification fires from one place. Renders nothing when checkout is
// unconfigured or no packs are available.
const BuyCreditsBody: React.FC<{ checkout: StripeCheckout }> = ({ checkout }) => {
  const { enabled, isLoading, error, startTopup } = checkout;
  const { showModal } = useModal();
  const { data: packs } = useTopupPacksQuery({ enabled });

  if (!enabled || !packs || packs.length === 0) return null;

  // The seeded packs share a validity window; surface it in the subtitle.
  const validityDays = packs.find((p) => p.validity_days)?.validity_days ?? 365;

  // Opened from the AI-credits usage card, which supplies the heading. The
  // subtitle stays for the 365-day validity, which nothing else states.
  return (
    <div className={styles.buyCreditsBlock}>
      <p className={styles.billingSubtitle}>
        <FormattedMessage
          {...messages.buyCreditsSectionSubtitle}
          values={{ validityDays }}
        />
      </p>
      <div className={styles.billingCards}>
        {packs.map((pack) => (
          <div key={pack.sku} className={styles.topupCard}>
            <span className={styles.billingCardValue}>
              <FormattedMessage
                {...messages.buyCreditsCardCredits}
                values={{ credits: formatNumberIntl(pack.credits) }}
              />
            </span>
            <span className={styles.billingCardSecondary}>
              <FormattedMessage
                {...messages.buyCreditsCardPrice}
                values={{ price: formatNumberIntl(pack.price_usd) }}
              />
            </span>
            <Button
              mode="primary"
              size="small"
              onClick={() => startTopup(pack.sku)}
              disabled={isLoading}
            >
              <FormattedMessage {...messages.buyCreditsCta} />
            </Button>
          </div>
        ))}
      </div>
      {isLoading && (
        <p className={styles.billingSubtitle} role="status">
          <FormattedMessage {...messages.buyCreditsStarting} />
        </p>
      )}
      {error && (
        <p className={styles.checkoutError} role="alert">
          <FormattedMessage {...messages.buyCreditsError} />
        </p>
      )}
      {/* Someone deciding whether $25 is worth it wants to know what a
          credit buys — same modal as the rate card's link. */}
      <button
        type="button"
        className={styles.cardAction}
        onClick={() => showModal(<AiCreditCostsModal />)}
      >
        <FormattedMessage {...messages.aiCostsLink} />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// The rail — every routine action in one place (Stripe-style): the plan CTA
// on top, then the secondary actions as links, then a Resources block. The
// main column keeps the information; this column is what you can DO about it.

// Anchor for the Resources link that scrolls rather than navigates.
const INVOICES_ANCHOR = 'subscription-invoices';

// Anchor for the inline purchase controls, so a `?purchase=` deep link brings
// the opened control into view instead of expanding it off-screen.
const PURCHASE_ANCHOR = 'subscription-purchase-controls';

// Where the "Team & users" Resources link goes. Group membership lives there
// now — multi-group subscriptions get a cross-group seat strip on that page.
const USERS_ROUTE = '/admin/users';

const scrollToAnchor = (id: string) =>
  window.setTimeout(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 0);

const RailActions: React.FC<{
  subscription: TSubscription;
  isOwner: boolean;
  checkout: StripeCheckout;
}> = ({ subscription, isOwner, checkout }) => {
  const intl = useIntl();
  const history = useHistory();
  const payment = useStripePayment();
  const offeredPlans = useOfferedPlans();
  const { data: topupPacks } = useTopupPacksQuery({ enabled: checkout.enabled });
  const { data: planCatalog } = usePlansQuery({ retry: false });
  // One purchase control open at a time, expanded inline under the links.
  // `?purchase=seats` (or `credits` / `spendLimit`) opens that control on
  // arrival. Deep-linked from elsewhere in the app — the invite modal's
  // "Add contributor seats" CTA — so the reader lands ON the control rather
  // than on a page of collapsed links they have to hunt through.
  const { search } = useLocation();
  const requestedPurchase = React.useMemo(() => {
    const value = new URLSearchParams(search).get('purchase');
    return value === 'seats' || value === 'credits' || value === 'spendLimit'
      || value === 'schemas'
      ? value
      : null;
  }, [search]);
  const [openPurchase, setOpenPurchase] =
    useState<'seats' | 'credits' | 'spendLimit' | 'schemas' | null>(
      requestedPurchase);
  // The initial state above only covers arriving WITH the parameter. A link
  // pushed while this page is already mounted — the spend-limit banner's CTA,
  // which sits a few hundred pixels above this rail — changes `search` without
  // remounting, so without this the button would appear to do nothing.
  React.useEffect(() => {
    if (!requestedPurchase) return;
    setOpenPurchase(requestedPurchase);
    scrollToAnchor(PURCHASE_ANCHOR);
  }, [requestedPurchase]);
  const togglePurchase = (
    which: 'seats' | 'credits' | 'spendLimit' | 'schemas',
  ) => setOpenPurchase((cur) => (cur === which ? null : which));

  if (!isOwner) return null;

  const status = subscription.status;
  const onTrial = subscription.details.is_trial || status === 'trialing';
  const selfServe = isSelfServePlan(subscription);

  // The one primary CTA: pick a plan (trial) / change it (paid). A pending
  // change or a cancellation parks it — those states are resolved from the
  // plan card (keep / reactivate), not by stacking another change.
  const showPlanCta = selfServe
    && !subscription.pending_plan
    && status !== 'cancelled'
    && offeredPlans.length > 0
    && (!onTrial || checkout.enabled);

  // Payment method — Stripe Customer Portal when a Customer exists; otherwise
  // the $0 card-capturing checkout ($0 PAYG for a trial). An enterprise
  // account with no Customer has nothing to offer: it is invoiced.
  const canUsePortal = subscription.has_billing_account !== false;
  const addCardPlan = onTrial ? 'PAYG' : subscription.plan;
  const showPaymentAction = payment.enabled && (canUsePortal || selfServe);
  const paymentBusy = payment.isLoading || checkout.isLoading;

  // Mirror each body's own render guard, so a link never opens an empty block.
  const seats = subscription.usage.users;
  const canBuySeats = selfServe && !onTrial && (seats.contributors_included ?? 0) > 0;
  const canBuyCredits = checkout.enabled && (topupPacks?.length ?? 0) > 0;
  // Schema add-on: only plans whose catalog entry sells it (PAYG) — branch
  // on `schema_addon` presence, never on the plan code.
  const currentCatalogPlan = planCatalog?.plans.find(
    (p) => p.code === subscription.plan,
  );
  const canBuySchemas = selfServe && !onTrial
    && Boolean(currentCatalogPlan?.schema_addon);
  // The spend limit is a self-serve billing control, so it follows the same
  // rule as the rest of this rail: enterprise accounts are invoiced under an
  // agreement and the server refuses to set one for them. A trial has no
  // metered charges to cap yet.
  const canSetSpendLimit = selfServe && !onTrial;

  if (!showPlanCta && !showPaymentAction && !canBuySeats && !canBuyCredits
      && !canSetSpendLimit && !canBuySchemas) {
    return null;
  }

  return (
    <AdminSettingsSection title={intl.formatMessage(messages.railActionsTitle)}>
      {showPlanCta && (
        <Button
          mode="primary"
          size="small"
          onClick={() => history.push(PLAN_PICKER_ROUTE)}
        >
          <FormattedMessage
            {...(onTrial ? messages.choosePlanCta : messages.changePlanButton)}
          />
        </Button>
      )}

      <div className={styles.railList} id={PURCHASE_ANCHOR}>
        {showPaymentAction && (
          <button
            type="button"
            className={styles.cardAction}
            onClick={canUsePortal
              ? payment.openPaymentManagement
              : () => checkout.startCheckout(addCardPlan)}
            disabled={paymentBusy}
          >
            <FormattedMessage
              {...(paymentBusy
                ? messages.updatePaymentStarting
                : canUsePortal
                  ? messages.updatePaymentCta
                  : messages.addPaymentCta)}
            />
          </button>
        )}
        {canBuyCredits && (
          <button
            type="button"
            className={styles.cardAction}
            aria-expanded={openPurchase === 'credits'}
            onClick={() => togglePurchase('credits')}
          >
            <FormattedMessage {...messages.buyCreditsSectionTitle} />
          </button>
        )}
        {canBuySeats && (
          <button
            type="button"
            className={styles.cardAction}
            aria-expanded={openPurchase === 'seats'}
            onClick={() => togglePurchase('seats')}
          >
            <FormattedMessage {...messages.seatsSectionTitle} />
          </button>
        )}
        {canBuySchemas && (
          <button
            type="button"
            className={styles.cardAction}
            aria-expanded={openPurchase === 'schemas'}
            onClick={() => togglePurchase('schemas')}
          >
            <FormattedMessage {...messages.schemasSectionTitle} />
          </button>
        )}
        {canSetSpendLimit && (
          <button
            type="button"
            className={styles.cardAction}
            aria-expanded={openPurchase === 'spendLimit'}
            onClick={() => togglePurchase('spendLimit')}
          >
            <FormattedMessage {...messages.spendCapTitle} />
          </button>
        )}
      </div>

      {openPurchase === 'credits' && <BuyCreditsBody checkout={checkout} />}
      {openPurchase === 'seats' && <SeatsBody subscription={subscription} />}
      {openPurchase === 'schemas' && <SchemasBody subscription={subscription} />}
      {openPurchase === 'spendLimit' && (
        <SpendLimitBody subscription={subscription} />
      )}

      {/* Enterprise with no Stripe Customer: invoiced under the agreement. */}
      {payment.enabled && !canUsePortal && !selfServe && (
        <p className={styles.railNote}>
          <FormattedMessage {...messages.paymentMethodEnterprise} />
        </p>
      )}
      {(payment.error || checkout.error) && (
        <p className={styles.checkoutError} role="alert">
          <FormattedMessage {...messages.paymentError} />
        </p>
      )}
    </AdminSettingsSection>
  );
};

// Resources — quiet links: the team page (where group membership lives), the
// invoice history, the list-rates reveal, and the cancel modal last.
const RailResources: React.FC<{
  subscription: TSubscription;
  isOwner: boolean;
}> = ({ subscription, isOwner }) => {
  const intl = useIntl();
  const history = useHistory();
  const { showModal } = useModal();
  // Same reveal pattern as Buy AI credits in the actions block: the link
  // toggles the rate card inline, directly under itself.
  const [ratesOpen, setRatesOpen] = useState(false);

  const onTrial = subscription.details.is_trial || subscription.status === 'trialing';
  // Enterprise is invoiced at negotiated rates — the published rate card
  // would be numbers it is not charged.
  const showRates = isSelfServePlan(subscription);

  return (
    <AdminSettingsSection title={intl.formatMessage(messages.railResourcesTitle)}>
      <div className={styles.railList}>
        <button
          type="button"
          className={styles.cardAction}
          onClick={() => history.push(USERS_ROUTE)}
        >
          <FormattedMessage {...messages.railTeamLink} />
        </button>
        {isOwner && (
          <button
            type="button"
            className={styles.cardAction}
            onClick={() => scrollToAnchor(INVOICES_ANCHOR)}
          >
            <FormattedMessage {...messages.invoicesHeading} />
          </button>
        )}
        {showRates && (
          <button
            type="button"
            className={styles.cardAction}
            aria-expanded={ratesOpen}
            onClick={() => setRatesOpen((open) => !open)}
          >
            <FormattedMessage {...messages.rateCardTitle} />
          </button>
        )}
        {/* Opens right under its own link, not below the rest of the list. */}
        {showRates && ratesOpen && (
          <RateCardBody planCode={subscription.plan} onTrial={onTrial} />
        )}
      </div>
    </AdminSettingsSection>
  );
};

// Invoice history — main column, owner-only. What used to be the "Payment &
// invoices" section minus the actions, which moved to the rail.
//
// This list is RECENT history, not an archive: the API returns one page plus
// Stripe's `has_more`. When there is more, we hand off to the Customer Portal
// rather than paginating here — the portal already does it, and it is the only
// surface that can show an unbounded history. Rendering a truncated list with
// no hand-off is the bug this replaces: it read as "these are all your
// invoices".
//
// `has_more` can be true with zero rendered rows (drafts occupy slots in the
// page and are dropped server-side for having no hosted URL), so the empty
// state and the hand-off are independent — never `else`-chained.
// "Cancel plan" — its own quiet footer at the very end of the main column,
// not a link among everyday Resources where it invites an accidental click.
// Always visible to an owner who can cancel; the modal still confirms.
const CancelPlanFooter: React.FC<{ subscription: TSubscription }> = ({ subscription }) => {
  const { showModal } = useModal();
  return (
    <div className={styles.cancelFooter}>
      <p className={styles.cancelFooterNote}>
        <FormattedMessage {...messages.cancelFooterNote} />
      </p>
      <button
        type="button"
        className={cn(styles.cardAction, styles.railMuted)}
        onClick={() => showModal(<CancelSubscriptionModal subscription={subscription} />)}
      >
        <FormattedMessage {...messages.cancelPlanCta} />
      </button>
    </div>
  );
};

const InvoicesSection: React.FC = () => {
  const intl = useIntl();
  const { data } = useInvoicesQuery();
  const invoices = data?.invoices ?? [];
  const hasInvoices = invoices.length > 0;
  const hasMore = !!data?.has_more;
  const payment = useStripePayment();
  // The portal needs a Stripe Customer, and `has_more` can only be true when
  // invoices exist — which means one does. So no extra binding check here.
  const canViewAll = hasMore && payment.enabled;

  return (
    <div id={INVOICES_ANCHOR}>
      <AdminSettingsSection title={intl.formatMessage(messages.invoicesHeading)}>
        {!hasInvoices && (
          <p className={styles.billingSubtitle}>
            <FormattedMessage {...messages.invoicesEmpty} />
          </p>
        )}
        {hasInvoices && (
          <ul className={styles.invoiceList}>
            {invoices.map((inv) => (
              <li key={inv.id ?? inv.invoice_url} className={styles.invoiceRow}>
                <span className={styles.invoiceDate}>
                  {inv.date ? formatToLocal(inv.date) : '—'}
                </span>
                <span className={styles.invoiceAmount}>{inv.amount ?? ''}</span>
                <a
                  className={styles.manageBillingLink}
                  href={inv.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage {...messages.invoiceDownload} />
                </a>
              </li>
            ))}
          </ul>
        )}
        {canViewAll && (
          <p className={styles.invoicesMore}>
            <FormattedMessage {...messages.invoicesViewAllHint} />{' '}
            <button
              type="button"
              className={styles.manageBillingLink}
              onClick={payment.openPaymentManagement}
              disabled={payment.isLoading}
            >
              <FormattedMessage {...messages.invoicesViewAll} />
            </button>
          </p>
        )}
        {canViewAll && payment.error && (
          <p className={styles.checkoutError} role="alert">
            <FormattedMessage {...messages.paymentError} />
          </p>
        )}
      </AdminSettingsSection>
    </div>
  );
};

const SubscriptionContent: React.FC<{
  subscription: TSubscription;
  isCurrentUserOwner: boolean;
}> = ({ subscription, isCurrentUserOwner }) => {
  const intl = useIntl();
  const { showSuccess } = useAlerts();
  // Catalog: lets the usage cards tell a ZERO allowance that is "metered from
  // the first unit" (PAYG) apart from one that is genuinely unlimited.
  const { data: planCatalog } = usePlansQuery({ retry: false });
  // Single Stripe checkout hook instance for the whole page (plan upgrade +
  // top-ups). It redirects to Stripe's hosted page and, on return, poll-
  // refreshes the subscription.
  const checkout = useStripeCheckout();

  // Purchase toast via the global alert system, driven from ground truth. Stripe
  // Checkout redirects away and back (full page reload), so we persist a baseline
  // {balance, plan} to sessionStorage the moment a purchase starts; on return the
  // hook poll-refreshes the subscription and this effect toasts once the balance
  // grows or the plan changes, then clears the baseline. A 5-minute stamp expires
  // an abandoned checkout so a later unrelated change can't fire it.
  // (The plan picker arms the same key before ITS checkouts, so an upgrade
  // bought there toasts here on return.)
  const BASELINE_KEY = BILLING_BASELINE_KEY;
  const currentBalance = subscription.usage?.ai_credits?.balance ?? 0;
  const currentPlan = subscription.plan;

  const arm = React.useCallback(() => {
    try {
      sessionStorage.setItem(
        BASELINE_KEY,
        JSON.stringify({ balance: currentBalance, plan: currentPlan, ts: Date.now() }),
      );
    } catch {
      /* sessionStorage unavailable — skip the toast, the data still refreshes */
    }
  }, [currentBalance, currentPlan]);

  React.useEffect(() => {
    let base: { balance: number; plan: string; ts: number } | null = null;
    try {
      const raw = sessionStorage.getItem(BASELINE_KEY);
      base = raw ? JSON.parse(raw) : null;
    } catch {
      base = null;
    }
    if (!base) return;
    const clear = () => {
      try {
        sessionStorage.removeItem(BASELINE_KEY);
      } catch {
        /* ignore */
      }
    };
    if (Date.now() - base.ts > 5 * 60 * 1000) {
      clear(); // abandoned checkout — expire the baseline
      return;
    }
    // Check the plan change first: an upgrade also grants credits, and the
    // plan message is the more relevant one. A pure top-up (same plan, more
    // credits) falls through to the credits toast.
    if (currentPlan !== base.plan) {
      clear();
      showSuccess(
        intl.formatMessage(messages.upgradeSuccess, {
          plan: subscription.details.name,
        }),
      );
    } else if (currentBalance > base.balance) {
      clear();
      showSuccess(
        intl.formatMessage(messages.topupSuccess, {
          credits: formatNumberIntl(currentBalance - base.balance),
        }),
      );
    }
  }, [currentBalance, currentPlan, intl, showSuccess, subscription.details.name]);

  // Wrap the starters so a baseline is persisted the moment a purchase begins
  // (survives the redirect to Stripe's hosted page).
  const armedCheckout: StripeCheckout = {
    ...checkout,
    startCheckout: (planCode: string) => {
      arm();
      return checkout.startCheckout(planCode);
    },
    startTopup: (sku: string) => {
      arm();
      return checkout.startTopup(sku);
    },
  };

  const billingCards = buildBillingCards(
    subscription,
    intl.formatMessage(messages.videoMinutesUnit),
    planCatalog?.plans.find((p) => p.code === subscription.plan),
  );
  const onTrial = subscription.details.is_trial || subscription.status === 'trialing';
  const trialLimitCards = onTrial ? buildTrialLimitCards(subscription) : [];

  // Corner pill marking a metric as billed beyond its allowance. Seats set
  // their own ("Seat cap") because they are refused, not billed.
  const billedTag = <FormattedMessage {...messages.billedTag} />;
  const trialLimitTag = <FormattedMessage {...messages.trialLimitTag} />;

  return (
    <>
      {/* Stripe Embedded Checkout modal (plan upgrade + top-ups), driven by the
          checkout hook's clientSecret. Rendered once for the whole page. */}
      <CheckoutModal
        clientSecret={checkout.clientSecret}
        onComplete={checkout.handleComplete}
        onClose={checkout.closeCheckout}
      />

      <StatusBanner subscription={subscription} checkout={armedCheckout} />

      {/* Two columns on desktop: MAIN = information (plan, usage), RAIL = the
          payment surface. Non-owners have no rail sections, so the grid drops
          the column rather than squeezing the usage cards beside a void. */}
      <div className={cn(styles.pageGrid, { [styles.pageGridNoRail]: !isCurrentUserOwner })}>
        <div className={styles.pageMain}>
          {/* 1. Overview — plan identity and live billing state (pending
              change / cancelled). Pure information; the actions rail carries
              everything you can do about it. */}
          <AdminSettingsSection title={intl.formatMessage(messages.planSectionTitle)}>
            <PlanOverview subscription={subscription} isOwner={isCurrentUserOwner} />
          </AdminSettingsSection>

          {/* 2. Usage this cycle — reporting only; the purchase controls for
              seats/credits live in the rail. A "Fair use" sub-group used to
              follow — removed: neither billed nor enforced on paid plans, so
              it only raised questions it then had to answer. */}
          <AdminSettingsSection title={intl.formatMessage(messages.usageSectionTitle)}>
            {/* The cycle's accrued metered charges in dollars — the one
                number PAYG customers actually want from this section. An
                estimate: cycle close nets AI drift against top-ups and
                applies the floor. Hidden on plans with nothing metered. */}
            {typeof subscription.estimated_usage_cost_usd === 'number' && (
              <p className={styles.billingSubtitle} data-test="usage-cost-estimate">
                <FormattedMessage
                  {...messages.usageEstimatedCost}
                  values={{
                    amount: subscription.estimated_usage_cost_usd.toFixed(2),
                  }}
                />
                {subscription.details.monthly_minimum_usd &&
                subscription.estimated_usage_cost_usd <
                  subscription.details.monthly_minimum_usd ? (
                  <>
                    {' '}
                    <FormattedMessage
                      {...messages.usageEstimatedCostFloorNote}
                      values={{
                        minimum: formatNumberIntl(
                          subscription.details.monthly_minimum_usd,
                        ),
                      }}
                    />
                  </>
                ) : null}
              </p>
            )}
            <div className={styles.billingCards}>
              {billingCards.map((card) => (
                <BillingCardView
                  key={card.key}
                  // "Billed" is the group default; a card that sets its own tag
                  // wins (seats are capped, not billed) — hence card LAST. A
                  // trial is never charged, so its allowances are limits.
                  card={onTrial
                    ? { tag: trialLimitTag, tagKind: 'capped', ...card }
                    : { tag: billedTag, tagKind: 'billed', ...card }}
                />
              ))}
            </div>

            {/* List rates used to fold here; it now lives in the rail's
                Resources block, revealed like Buy AI credits. */}

            {/* "A plan would be cheaper this month" — directly under the usage
                it is computed from, so the claim can be checked against the
                cards above it rather than taken on trust. Renders nothing
                unless the server found a genuinely cheaper plan. */}
            <UpgradeNudge subscription={subscription} />

            {/* Trial-only: the two dimensions that are fair use everywhere else
                but hard walls on DEMO. Off-trial this renders nothing. */}
            {trialLimitCards.length > 0 && (
              <>
                <h4 className={styles.usageGroupHeading}>
                  <FormattedMessage {...messages.trialLimitsHeading} />
                </h4>
                <p className={styles.billingSubtitle}>
                  <FormattedMessage
                    {...(offeredPlans(planCatalog?.plans).length > 0
                      ? messages.trialLimitsSubtitle
                      : messages.trialLimitsSubtitleNoPlans)}
                  />
                </p>
                <div className={styles.billingCards}>
                  {trialLimitCards.map((card) => (
                    <BillingCardView
                      key={card.key}
                      card={{ tag: trialLimitTag, tagKind: 'capped', ...card }}
                    />
                  ))}
                </div>
              </>
            )}
          </AdminSettingsSection>

          {/* 3. Invoice history (owner) — the Resources "Invoices" link
              scrolls here. */}
          {isCurrentUserOwner && <InvoicesSection />}

          {/* 4. Cancel — last thing on the page, deliberately. */}
          {canCancelPlan(subscription, isCurrentUserOwner) && (
            <CancelPlanFooter subscription={subscription} />
          )}
        </div>

        {/* The rail: actions on top (plan CTA, payment method, buy credits,
            seats — with the purchase controls expanding inline), then the
            Resources links. Group membership is no longer on this page at
            all — it lives with the team on /admin/users, where multi-group
            subscriptions get a cross-group seat strip. */}
        {isCurrentUserOwner && (
          <aside className={styles.pageRail}>
            <RailActions
              subscription={subscription}
              isOwner={isCurrentUserOwner}
              checkout={armedCheckout}
            />
            <RailResources subscription={subscription} isOwner={isCurrentUserOwner} />
          </aside>
        )}
      </div>
    </>
  );
};

export const Subscription: React.FC = () => {
  const intl = useIntl();
  const auth = useAuthUser();
  const currentUserId = auth()?.userId;
  const { data, isLoading, error, refetch, isFetching } = useSubscriptionQuery();

  const renderShell = (body: React.ReactNode) => (
    <div className={styles.pageWrapper}>
      <PageTitle
        className={styles.pageTitle}
        title={intl.formatMessage(messages.pageTitle)}
      />
      <PageBody>{body}</PageBody>
    </div>
  );

  if (isLoading) {
    return renderShell(
      <div className={styles.loadingContainer}>
        <Loader />
      </div>,
    );
  }

  const status = getErrorStatus(error);

  if (status === 403) {
    return renderShell(
      <div className={styles.infoBanner}>
        <FormattedMessage {...messages.forbidden} />
      </div>,
    );
  }

  if (status === 404) {
    return renderShell(
      <div className={styles.errorBanner}>
        <FormattedMessage {...messages.notFound} />
      </div>,
    );
  }

  if (error || !data) {
    return renderShell(
      <div className={styles.errorBanner}>
        <span>
          <FormattedMessage {...messages.loadError} />
        </span>
        <Button mode="secondary" size="small" onClick={() => refetch()} disabled={isFetching}>
          <FormattedMessage {...messages.retry} />
        </Button>
      </div>,
    );
  }

  const isCurrentUserOwner =
    currentUserId !== undefined && Number(currentUserId) === data.owner_user_id;

  return renderShell(
    <SubscriptionContent subscription={data} isCurrentUserOwner={isCurrentUserOwner} />,
  );
};

export default Subscription;
